import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { BaseClass } from '../../class/baseClass';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToasterService } from '../../../services/toaster.service';
import { StripeService } from '../../../services/stripe.service';
import { Stripe, StripeCardElement } from '@stripe/stripe-js';
import { AuthService } from '../../../services/auth.service';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'mindbase-invoice-payout',
  standalone: true,
  imports: [ 
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    TranslateModule,
    RouterModule,
    FormsModule,
    MatIconModule
  ],
  templateUrl: './invoice-payout.component.html',
  styleUrl: './invoice-payout.component.scss'
})
export class InvoicePayoutComponent extends BaseClass implements OnInit, OnDestroy {
  TENANT_PAYMENT_TYPE = {
    SUBSCRIPTION: 1, // new or renew
    APP_LAUNCH: 2,
    UPGRADE_DOWNGRADE: 3,
    ADMIN: 4
  };
  PAYMENT_STATUS = {
    PENDING: 1,
    INITIATE: 5,
    CARD_ADDED: 6,
    SUBSCRIPTION_CREATED: 7,
    SUCCESS: 2,
    FAILED: 3,
    DECLINED: 4, // cancelled
    VOID: 8
  };
  private destroy: Subject<boolean> = new Subject<boolean>();
  protected invoiceId: any;
  protected tenantId: any;
  protected invoiceType: any;
  protected origin: any;
  protected invoiceT = this.TENANT_PAYMENT_TYPE;
  protected invoiceAmount: any;
  protected  clientSecret: string = 'pi_xxx_secret_xxx';
  protected stripe: Stripe | null = null;
  protected card: StripeCardElement | null = null;
  protected saveCard: boolean = true;
  protected isCardComplete: boolean = false;
  protected isFailed: boolean = false;
  protected isDeclined: boolean = false;
  protected isVoid: boolean = false;
  protected isPaid: boolean = false;
  
  constructor( 
    injector: Injector,
     private _authService: AuthService,
    private _translateService: TranslateService,
    private _toasterService:ToasterService,
    private stripeService: StripeService
  ) {
    super(injector);
    this.invoiceId = this.route.snapshot.queryParamMap.get('invoiceId');
    this.tenantId = this.route.snapshot.queryParamMap.get('tenantId');
    this.invoiceAmount = this.route.snapshot.queryParamMap.get('amount');
    this.invoiceType = this.route.snapshot.queryParamMap.get('invoiceType');
    this.origin = this.route.snapshot.queryParamMap.get('origin');
    this.getPaymentStatus(this.invoiceId);
  }

  ngOnInit(): void {
    this.loadStripe();
  }

  getPaymentStatus(invoiceId: string): void {
    this.spinnerService.addToLoader('getRecentFiles');
    this._authService
      .getPaymentStatus(invoiceId)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: any) => {
          this.spinnerService.removeFromLoader('getRecentFiles');          
          this.isPaid = res.data === this.PAYMENT_STATUS.SUCCESS;
          this.isFailed = res.data === this.PAYMENT_STATUS.FAILED;
          this.isDeclined = res.data === this.PAYMENT_STATUS.DECLINED;
          this.isVoid = res.data === this.PAYMENT_STATUS.VOID;
        },
        error: () => {
          this.spinnerService.removeFromLoader('getRecentFiles');
        }
      });
  }

  async loadStripe(): Promise<void> {
    if (this.stripe && this.card) return; // Avoid double mount

    this.stripe = await this.stripeService.getStripe();
    if (this.stripe && document.getElementById('card-element')) {
      const elements = this.stripe.elements();
      this.card = elements.create('card', {
        hidePostalCode: true
      });
      this.card.mount('#card-element');

      // Listen for changes
      this.card.on('change', (event) => {
        this.isCardComplete = event.complete;
      });
    } else {
      console.error('Card element not found or Stripe failed to load.');
    }
  }

  getInvoiceType(type: any): string {
    const normalizedType = isNaN(Number(type)) ? type : Number(type);
    switch (normalizedType) {
    case this.TENANT_PAYMENT_TYPE.SUBSCRIPTION:
      return 'Payment for Subscription';
    case this.TENANT_PAYMENT_TYPE.APP_LAUNCH:
      return 'Payment for App Launch';
    case this.TENANT_PAYMENT_TYPE.UPGRADE_DOWNGRADE:
      return 'Payment for Subscription Update';
    case this.TENANT_PAYMENT_TYPE.ADMIN:
      return 'Payment created by admin.';
    default:
      return 'Unknown';
    }
  }

  async payOut(): Promise<void> {
    if (!this.stripe || !this.card) {
      await this.loadStripe(); // Ensure Stripe is ready
    }
    if (!this.isCardComplete) {
      this._toasterService.errToastr(
        this._translateService.instant('Please enter valid card details.')
      );
      return;
    }
    const data = {
      tenantId : this.tenantId,
      invoiceType : this.invoiceType,
      amount : this.invoiceAmount,
      invoiceId : this.invoiceId
    };
    this.spinnerService.addToLoader('makePayment');
    this._authService.invoicePayOut(data).pipe(takeUntil(this.destroy)).subscribe({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      next: (res: any) => {
        if (res) {
          this.clientSecret = res.data.paymentIntent.client_secret;
          this.handlePayment();
        }
      },
      error: () => {
        this.spinnerService.removeFromLoader('makePayment');
      }
    });
  }

  async handlePayment(): Promise<void> {
    if (!this.stripe || !this.card) return;

    try {
      const result = await this.stripeService.confirmPayment(
        this.clientSecret,
        this.card,
        this.saveCard
      );

      if (result.error) {
        alert(`Payment failed: ${result.error.message}`);
      } else if (result.paymentIntent?.status === 'succeeded') {
        this.spinnerService.removeFromLoader('makePayment');
        this._toasterService.successToastr(
          this._translateService.instant('Invoice Payment Successfully')
        );
        localStorage.clear();
        this.router.navigate(['/auth/login']);
      }
    } catch (error) {
      console.error('Error during payment:', error);
    }
  }


  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }

}
